<template>
  <section id="artists" ref="root" class="artists">
    <div ref="backdrop" class="artists__backdrop" aria-hidden="true"></div>
    <div class="artists__shade" :class="{ 'is-active': hoveredId !== null }" aria-hidden="true"></div>

    <div class="artists__content">
      <SectionHeading index="03" title="Artists">
        Our roster. Tap an artist to request a booking.
      </SectionHeading>

      <div v-if="!loading && roster.length === 0" class="empty-state" data-reveal>
        No hay artistas disponibles
      </div>

      <!-- Desktop: lista de nombres gigantes -->
      <ul
        v-if="roster.length"
        class="roster"
        :class="{ 'has-hover': hoveredId !== null }"
        @pointerleave="clearBackdrop"
      >
        <li v-for="(member, i) in roster" :key="member.id" class="roster__item" data-reveal>
          <button
            v-cursor="'Book'"
            type="button"
            class="roster__row"
            :class="{ 'is-hovered': hoveredId === member.id }"
            @pointerenter="onEnter($event, member)"
            @click="openBooking(member, $event)"
          >
            <span class="roster__index">{{ pad(i + 1) }}</span>
            <span class="roster__name">{{ member.name }}</span>
            <span class="roster__book">
              <span class="roll"><span data-text="Book now">Book now</span></span>
              <span class="arrow" aria-hidden="true">↗</span>
            </span>
          </button>
        </li>
      </ul>

      <!-- Mobile / tablet: tarjetas -->
      <div v-if="roster.length" class="cards">
        <article v-for="(member, i) in roster" :key="`card-${member.id}`" class="card">
          <div class="card__media" data-reveal="image">
            <img :src="member.image_url" :alt="member.name" loading="lazy">
          </div>
          <div class="card__meta">
            <span class="card__index">{{ pad(i + 1) }}</span>
            <h3 class="card__name">{{ member.name }}</h3>
          </div>
          <button type="button" class="pill card__book" @click="openBooking(member, $event)">
            Contact {{ firstName(member) }}
            <span class="arrow" aria-hidden="true">↗</span>
          </button>
        </article>
      </div>
    </div>

    <BookingModal
      v-if="booking"
      :member="booking.member"
      :origin="booking.origin"
      @close="booking = null"
    />
  </section>
</template>

<script>
import { gsap, isTouchDevice, prefersReducedMotion } from '@/animations/gsap'
import { setupCircle, tweenCircle, MIN_SCALE } from '@/animations/circleReveal'
import { setCursorLabel } from '@/animations/cursor'
import { apiUrl } from '@/services/apiConfig'
import motion from '@/mixins/motion'
import SectionHeading from '@/components/ui/SectionHeading.vue'
import BookingModal from '@/components/BookingModal.vue'

export default {
  name: 'RosterGroup',
  components: { SectionHeading, BookingModal },
  mixins: [motion],
  data() {
    return {
      roster: [],
      loading: true,
      error: null,
      hoveredId: null,
      booking: null
    }
  },
  mounted() {
    this.revealContent()
    this.fetchDJs()
  },
  beforeDestroy() {
    this.clearBackdrop(true)
  },
  methods: {
    pad(n) {
      return String(n).padStart(2, '0')
    },
    firstName(member) {
      return (member.name || '').split(' ')[0]
    },
    async fetchDJs() {
      try {
        this.loading = true
        this.error = null
        const response = await fetch(apiUrl('/api/djs'))
        if (!response.ok) throw new Error('Error al cargar los artistas')
        this.roster = await response.json()
        this.preload()
      } catch (error) {
        console.error('Error fetching DJs:', error)
        this.error = 'No se pudieron cargar los artistas'
        this.roster = []
      } finally {
        this.loading = false
        this.revealContent()
      }
    },
    // Precarga las fotos para que el círculo no se abra sobre una imagen vacía.
    preload() {
      if (isTouchDevice()) return
      const run = () => this.roster.forEach((m) => {
        const img = new Image()
        img.src = m.image_url
      })
      if ('requestIdleCallback' in window) window.requestIdleCallback(run)
      else setTimeout(run, 1200)
    },
    onEnter(e, member) {
      if (e.pointerType !== 'mouse' || this.hoveredId === member.id) return
      this.hoveredId = member.id
      this.revealBackdrop(member, e)
    },
    // Abre la foto del artista en un círculo que crece desde el cursor.
    revealBackdrop(member, e) {
      const root = this.$refs.root
      const host = this.$refs.backdrop
      const rect = root.getBoundingClientRect()
      const area = { width: rect.width, height: rect.height }
      const origin = { x: e.clientX - rect.left, y: e.clientY - rect.top }

      const mask = document.createElement('div')
      const counter = document.createElement('div')
      const img = document.createElement('img')
      img.src = member.image_url
      img.alt = ''
      mask.className = 'artists__layer'
      counter.appendChild(img)
      mask.appendChild(counter)
      host.appendChild(mask)

      if (prefersReducedMotion()) {
        setupCircle(mask, counter, area, origin, 1)
        gsap.from(mask, { opacity: 0, duration: 0.3, onComplete: () => this.pruneLayers(mask) })
        return
      }

      const circle = setupCircle(mask, counter, area, origin, MIN_SCALE)
      tweenCircle(circle, 1, { duration: 1.2, ease: 'expo.out', onComplete: () => this.pruneLayers(mask) })
      gsap.fromTo(img, { scale: 1.3 }, { scale: 1.05, duration: 1.8, ease: 'expo.out' })
    },
    pruneLayers(keep) {
      const host = this.$refs.backdrop
      if (!host) return
      Array.from(host.children).forEach((layer) => {
        if (layer !== keep && layer.compareDocumentPosition(keep) & Node.DOCUMENT_POSITION_FOLLOWING) {
          host.removeChild(layer)
        }
      })
    },
    clearBackdrop(immediate) {
      this.hoveredId = null
      const host = this.$refs.backdrop
      if (!host || !host.children.length) return
      const layers = Array.from(host.children)
      if (immediate === true) {
        layers.forEach((l) => host.removeChild(l))
        return
      }
      gsap.to(layers, {
        opacity: 0,
        duration: 0.6,
        ease: 'power2.out',
        onComplete: () => layers.forEach((l) => l.parentNode && l.parentNode.removeChild(l))
      })
    },
    openBooking(member, e) {
      const isKeyboard = e && e.detail === 0
      const target = e && e.currentTarget
      let origin = null
      if (!isKeyboard && e) origin = { x: e.clientX, y: e.clientY }
      else if (target) {
        const r = target.getBoundingClientRect()
        origin = { x: r.left + r.width / 2, y: r.top + r.height / 2 }
      }
      setCursorLabel('')
      this.booking = { member, origin }
    }
  }
}
</script>

<style scoped>
.artists {
  position: relative;
  z-index: 1;
  background: var(--c-bg);
  padding: calc(var(--section-space) * 0.6) 0 var(--section-space);
  overflow: hidden;
  isolation: isolate;
}

.artists__backdrop {
  position: absolute;
  inset: 0;
  z-index: -2;
  pointer-events: none;
}

.artists__backdrop >>> .artists__layer img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: grayscale(1) contrast(1.05);
}

.artists__shade {
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(ellipse at center, transparent 20%, rgba(0, 0, 0, 0.6) 100%),
    rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity 0.6s var(--ease-out);
}

.artists__shade.is-active {
  opacity: 1;
}

.empty-state {
  padding: 4rem var(--gutter);
  color: var(--c-muted);
  font-size: var(--fs-lead);
}

/* ---------- Lista desktop ---------- */

.roster {
  list-style: none;
  margin-top: 1rem;
  padding: 0 var(--gutter);
}

.roster__item {
  border-bottom: 1px solid var(--c-line);
}

.roster__row {
  width: 100%;
  display: grid;
  grid-template-columns: 4rem 1fr auto;
  align-items: center;
  gap: 1.5rem;
  padding: clamp(1rem, 0.5rem + 1.2vw, 2rem) 0;
  text-align: left;
  color: #fff;
  transition: opacity 0.5s var(--ease-out);
}

.roster__index {
  font-size: var(--fs-small);
  letter-spacing: 0.1em;
  color: var(--c-accent);
  align-self: start;
  padding-top: 0.6em;
}

.roster__name {
  font-size: clamp(2.6rem, 1rem + 6.4vw, 8.5rem);
  line-height: 0.92;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  transition: transform 0.6s var(--ease-out);
}

.roster__book {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0;
  transform: translateX(-1rem);
  transition: opacity 0.5s var(--ease-out), transform 0.6s var(--ease-out);
}

@media (hover: hover) {
  .roster.has-hover .roster__row:not(.is-hovered) {
    opacity: 0.25;
  }

  .roster__row.is-hovered .roster__name {
    transform: translateX(1.5rem);
  }

  .roster__row.is-hovered .roster__book {
    opacity: 1;
    transform: none;
  }
}

/* ---------- Tarjetas mobile ---------- */

.cards {
  display: none;
}

@media (max-width: 900px), (hover: none) {
  .roster {
    display: none;
  }

  .cards {
    display: flex;
    gap: 1.25rem;
    margin-top: 2.5rem;
    padding: 0 var(--gutter);
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding: 0 var(--gutter);
    scrollbar-width: none;
  }

  .cards::-webkit-scrollbar {
    display: none;
  }

  .card {
    flex: 0 0 min(78vw, 24rem);
    scroll-snap-align: start;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .card__media {
    aspect-ratio: 4 / 5;
    overflow: hidden;
    background: var(--c-surface);
  }

  .card__media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .card__meta {
    display: flex;
    align-items: baseline;
    gap: 0.8rem;
  }

  .card__index {
    font-size: var(--fs-small);
    color: var(--c-accent);
    letter-spacing: 0.1em;
  }

  .card__name {
    font-size: 1.6rem;
    font-weight: 400;
    text-transform: uppercase;
    line-height: 1;
  }

  .card__book {
    align-self: flex-start;
  }
}
</style>
